import i18next from 'i18next';
import React from 'react';

import { escapeRegExp } from '../../utils/text/text';
import { AssetManager } from '../../viewer/asset-manager';
import { AssetType } from '../../viewer/elements/element-obj';
import { SubscriptionTopic } from '../../viewer/messaging/pubsub';
import { useSubscribe } from './use-subscribe';

/**
 * Returns the next asset name as "Untitled <int>" where <int> is the max number among the
 * existing matching asset names + 1.
 */
export const useDefaultAssetName = (assetType: AssetType, currentAssetId?: string) => {
  const assetListChangedMessage = useSubscribe(SubscriptionTopic.AssetListChanged);

  const nextDefaultAssetName = React.useMemo(() => {
    return getNextDefaultName(assetType, currentAssetId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetType, assetListChangedMessage]);

  return nextDefaultAssetName;
};

export const getNextDefaultName = (assetType: AssetType, currentAssetId?: string) => {
  const defaultName = (i18next?.t('untitled', { ns: 'common' }) as string) ?? 'Ej namngiven';

  // Fetch all numbers in pattern "Untitled <int>".
  const regex = new RegExp(`${escapeRegExp(defaultName)} ([0-9]+)$`, 'i');
  const numbers = AssetManager.instance
    .getAssets()
    .filter((x) => x.assetType === assetType && x.assetId !== currentAssetId)
    .map((x) => x.name.trim())
    .filter((name) => regex.test(name))
    .map((name) => Number(regex.exec(name)![1]));

  // Return new default name.
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
  return `${defaultName} ${maxNumber + 1}`;
};
